import { CursorValueAdjustableComponentProps } from "@multiweb/reactComponents";
import { GenericControlConnector } from "@multiweb/reactConnectors/GenericControlConnector";
import { RootComponentClass } from "@multiweb/reactSetup";
import { Cursor } from "@multiweb/types/multiweb";
import { ReactComponent } from "@multiweb/types/react";
import { AnyConstructor } from "Global/Base/types";
import { IItemRightsCollection } from "../../../Global/Base/Rights/RightTypes";
import { EventsController } from "../../../Global/Dom/EventsController";
import { ICursorValueAdjustable } from "../../UI/UI/Controls/ControlInterfaces";

/**
 * Přidá podporu pro nastavování hodnoty kurzoru k zadanému konektoru, jinými slovy podporuje pouze konektory pro react komponenty.
 *
 * Poznámka:
 * Momentálně obsahuje workaround spojený s TS issue 17293, viz komentář dole.
 *
 * @param Base Základní komponenta, která se rozšíří o podporu nastavování hodnoty kurzoru.
 */
export const CursorValueAdjustableConnectorMixin = <T extends AnyConstructor<GenericControlConnector>>
    (Base: T) => class CursorValueAdjustableConnectorMixin extends Base implements ICursorValueAdjustable {
        /**
         * Hodnota kurzoru myši
         */
        public _cursor: Cursor;

        // Properties dole se v rámci mixinu aktivně nevyužívají, jsou tu kvůli spokojenosti compileru.
        // Současná verze typescriptu v MW (v době psaní TS 4.1.2) zatím nedokáže pracovat s private/protected propertami tříd v rámci mixinu a jejich doplnění zde je pouze WORKAROUND.
        // Problém tu je způsoben tím, že generický typ T extenduje konstruktor třídy GenericControlConnector, v jiných mixinech se používají jenom rozhraní.
        // Problém má otevřenou issue https://github.com/Microsoft/TypeScript/issues/17293, ale zatím jeho oprava není zaplánována do dalších verzí TS.
        // Po jejím uzavření bude nutné tento mixin upravit. Stejně tak bude potřeba odstranit první řádek, který u tohoto souboru zakazuje kontrolu ESLintem.
        // Pokud by se properties z mixinu vymazaly, compiler by křičel s hláškou error TS4094: Property 'xyz' of exported class expression may not be private or protected.
        // Vymazat by se tak měly z tohoto mixinu po zavření issue následující properties - _rootElement, _rootComponent, _component, _isCompositeComponent, _originalRightsDefinition, _render a _renderIfTrue.
        /** @protected */
        public override _rootElement: HTMLElement;
        /** @protected */
        public override _rootComponent: RootComponentClass;
        /** @protected */
        public override _component: ReactComponent;
        /** @protected */
        public override _isCompositeComponent?: boolean;
        /** @protected */
        public override _originalRightsDefinition: IItemRightsCollection;
        /** @protected */
        public override _eventsController: EventsController;

        public get cursor(): Cursor {
            return this._cursor;
        }

        public set cursor(cursor: Cursor) {
            this._cursor = cursor;
            this._render();
        }

        /**
         * Zkonvertuje atributy ovlivňující podobu vykreslované react komponenty do props a přidává navíc prop `cursor`.
         * Prop `cursor` se zde konvertuje z obecného typu `CursorValue` na hodnotu reactího enumu `Cursor`.
         * @protected
         */
        public override _getRenderProps(): CursorValueAdjustableComponentProps {
            return {
                ...super._getRenderProps(),
                cursor: this.cursor,
            };
        }

        /** @protected */
        public override _render(): void {
            super._render();
        }

        /** @protected */
        public override _renderIfTrue(canRender: boolean): void {
            super._renderIfTrue(canRender);
        }
    };
